import React, { useMemo, useState } from "react";
import { Button, Modal, ModalHeader, ModalBody, Row, Col } from "reactstrap";
import UserDataTable from "components/TableContainers/UserDataTable";
import { formatISOToDDMMYYYY } from "helpers/dateFormat_helper";
import ConfirmationModal from "components/Modals/ConfirmationModal";
import { FaCheck, FaEye } from "react-icons/fa";

const PendingVendorTable = ({ vendors, loading, onApprove, hasApprovePermission }) => {
    const [selectedSortData, setSelectedSortData] = useState({ value: "created_at", direction: "desc" });
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [searchString, setSearchString] = useState("");
    const [approveId, setApproveId] = useState(null);
    const [openModal, setOpenModal] = useState(false);
    
    const [viewVendor, setViewVendor] = useState(null);
    const [openViewModal, setOpenViewModal] = useState(false);

    const handleApproveClick = (id) => {
        setApproveId(id);
        setOpenModal(true);
    };

    const handleViewClick = (vendor) => {
        setViewVendor(vendor);
        setOpenViewModal(true);
    };

    const confirmApprove = () => {
        onApprove(approveId);
        setOpenModal(false);
    };

    const columns = useMemo(
        () => [
            {
                header: "Store Name",
                accessorKey: "store_name",
            },
            {
                header: "Owner Name",
                accessorKey: "name",
            },
            {
                header: "Email",
                accessorKey: "email",
            },
            {
                header: "Mobile",
                accessorKey: "mobile",
            },
            {
                header: "ID Proof",
                accessorKey: "id_proof_file",
                cell: ({ row }) => (
                    row.original.id_proof_file ? (
                        <a 
                            href={`${process.env.REACT_APP_BASE_URL}${row.original.id_proof_file.replace(/^\//, '')}`} 
                            target="_blank" 
                            rel="noreferrer"
                            className="text-primary"
                        >
                            View Document
                        </a>
                    ) : (
                        <span className="text-muted">No File</span>
                    )
                ),
            },
            {
                header: "Joined At",
                accessorKey: "createdAt",
                cell: ({ row }) => (
                    <span>{formatISOToDDMMYYYY(row.original.createdAt)}</span>
                ),
            },
            {
                header: "Action",
                id: "action",
                cell: ({ row }) => (
                    <div className="d-flex gap-2">
                        <Button
                            color="info"
                            title="View Details"
                            onClick={() => handleViewClick(row.original)}
                        >
                            <FaEye size={16} />
                        </Button>
                        {hasApprovePermission && (
                            <Button
                                color="success"
                                title="Approve"
                                onClick={() => handleApproveClick(row.original._id)}
                            >
                                <FaCheck size={16} className="me-1" /> Approve
                            </Button>
                        )}
                    </div>
                ),
            },
        ],
        [hasApprovePermission]
    );

    const InfoRow = ({ label, value }) => (
        <Row className="mb-2 border-bottom pb-1">
            <Col md={5} className="fw-bold text-muted">{label}:</Col>
            <Col md={7}>{value || "N/A"}</Col>
        </Row>
    );

    return (
        <React.Fragment>
            <ConfirmationModal
                okText="Approve"
                onCancel={() => setOpenModal(false)}
                onOk={confirmApprove}
                isVisible={openModal}
                title="Approve Vendor"
                content="Are you sure you want to approve this vendor? They will be able to log in and manage their store."
            />

            {/* View Details Modal */}
            <Modal isOpen={openViewModal} toggle={() => setOpenViewModal(false)} size="lg">
                <ModalHeader toggle={() => setOpenViewModal(false)}>Vendor Details - {viewVendor?.store_name}</ModalHeader>
                <ModalBody className="p-4">
                    {viewVendor && (
                        <Row>
                            <Col md={6}>
                                <h5 className="text-primary mb-3">Personal Information</h5>
                                <InfoRow label="Full Name" value={viewVendor.name} />
                                <InfoRow label="Email" value={viewVendor.email} />
                                <InfoRow label="Mobile" value={viewVendor.mobile} />
                                <InfoRow label="WhatsApp" value={viewVendor.whatsapp_number} />
                                
                                <h5 className="text-primary mt-4 mb-3">Business Details</h5>
                                <InfoRow label="Store Name" value={viewVendor.store_name} />
                                <InfoRow label="Business Name" value={viewVendor.business_name} />
                                <InfoRow label="TL Number" value={viewVendor.tl_number} />
                                <InfoRow label="VAT Number" value={viewVendor.vat_number} />
                                <InfoRow label="ID Proof Number" value={viewVendor.id_proof_number} />
                                <div className="mb-3">
                                    <span className="fw-bold text-muted">Corporate Address:</span>
                                    <p className="mt-1 p-2 bg-light rounded">{viewVendor.corporate_address}</p>
                                </div>
                            </Col>
                            <Col md={6}>
                                <h5 className="text-primary mb-3">Bank Details</h5>
                                <InfoRow label="Bank Name" value={viewVendor.bank_name} />
                                <InfoRow label="Branch" value={viewVendor.bank_branch} />
                                <InfoRow label="Account Name" value={viewVendor.account_name} />
                                <InfoRow label="Account Number" value={viewVendor.account_number} />
                                <InfoRow label="IBAN" value={viewVendor.iban} />

                                <h5 className="text-primary mt-4 mb-3">Documents</h5>
                                <div className="p-3 border rounded text-center">
                                    {viewVendor.id_proof_file ? (
                                        <div>
                                            <p className="mb-2">ID Proof Document is available.</p>
                                            <a 
                                                href={`${process.env.REACT_APP_BASE_URL}${viewVendor.id_proof_file.replace(/^\//, '')}`} 
                                                target="_blank" 
                                                rel="noreferrer"
                                                className="btn btn-outline-primary btn-sm"
                                            >
                                                View ID Proof
                                            </a>
                                        </div>
                                    ) : (
                                        <p className="text-muted mb-0">No document uploaded.</p>
                                    )}
                                </div>
                            </Col>
                        </Row>
                    )}
                </ModalBody>
            </Modal>

            <div className="container-fluid">
                <UserDataTable
                    columns={columns}
                    loading={loading}
                    data={vendors || []}
                    isGlobalFilter
                    isPagination
                    selectedSortData={selectedSortData}
                    setSelectedSortData={setSelectedSortData}
                    pageIndex={pageIndex}
                    setPageIndex={setPageIndex}
                    setPageSize={setPageSize}
                    pageSize={pageSize}
                    setSearchString={setSearchString}
                    searchString={searchString}
                    SearchPlaceholder="Search pending vendors..."
                    pagination="pagination"
                    docName="Pending Vendors"
                    totalrows={vendors?.length || 0}
                    paginationWrapper="dataTables_paginate paging_simple_numbers"
                    tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                />
            </div>
        </React.Fragment>
    );
};

export default PendingVendorTable;
